import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, catchError, finalize, forkJoin, of, takeUntil } from 'rxjs';
import { TextEditorWrapperComponent } from 'src/app/shared/components/text-editor/text-editor-wrapper/text-editor-wrapper.component';
import {
  LONGEVITY_AREA_LABELS,
  LONGEVITY_COMMENT_MAX_LENGTH,
  LONGEVITY_GRADE_LABELS,
  LONGEVITY_PLAN_MAX_LENGTH,
  MANAGER_CASE_STATUS_LABELS,
  LongevityAreaId,
  LongevityAreaScore,
  LongevityAssessmentDetail,
  LongevityGrade,
  LongevityTestRow,
  ManagerCaseStatus,
} from '../../../alerts/longevity-alerts/longevity-alerts.model';
import {
  buildAnswerViews,
  editorHtmlToMarkdown,
  markdownToEditorHtml,
} from './longevity-request-assessments.mapper';
import { LongevityRequestAssessmentsService } from './longevity-request-assessments.service';
import { groupAnswersBySection, LONGEVITY_AREA_ICONS, LongevitySectionView } from './longevity-sections';

/**
 * Modale di un test Longevity: riepilogo dei punteggi, domande divise per sezione con la risposta data sotto
 * ciascuna, e due editor di testo formattato (commento e piano) salvati con il PATCH sul singolo test. Il
 * dettaglio si carica quando la modale si apre (aprire un test con un caso finisce nel log degli accessi).
 * Lo usano l'elenco dei test di una richiesta ("Vedi risposte") e la coda della pagina Avvisi ("Dettaglio").
 */
@Component({
  selector: 'app-longevity-assessment-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TextEditorWrapperComponent],
  templateUrl: './longevity-assessment-modal.component.html',
  styleUrl: './longevity-assessment-modal.component.scss',
})
export class LongevityAssessmentModalComponent implements OnChanges, OnDestroy {
  /** Il test da mostrare: ogni volta che cambia si ricarica il dettaglio. */
  @Input({ required: true }) assessment!: LongevityTestRow;
  /** Chiusura richiesta dall'utente (bottone o sfondo): il genitore toglie la modale. */
  @Output() closed = new EventEmitter<void>();
  /** Il dettaglio è arrivato o è stato aggiornato dal salvataggio di commento e piano. */
  @Output() detailChanged = new EventEmitter<LongevityAssessmentDetail>();

  /** Il test per intero, appena arriva dal backend (risultato, risposte, commento e piano). */
  detail: LongevityAssessmentDetail | null = null;
  detailLoading = false;
  detailFailed = false;
  sections: LongevitySectionView[] = [];

  /**
   * Solo ciò che il Markdown di commento/piano sa esprimere (titoli, grassetto, elenco puntato): la Web App
   * non interpreta HTML, quindi altri stili andrebbero persi in silenzio.
   */
  readonly editorModules = {
    toolbar: [[{ header: 3 }], ['bold'], [{ list: 'bullet' }]],
  };

  /** Commento e piano del test aperto: HTML dell'editor. `dirty` si accende solo per modifiche dell'utente. */
  commentControl = new FormControl<string | null>('');
  planControl = new FormControl<string | null>('');
  notesSaving = false;
  notesSaved = false;
  /** Perché il salvataggio non è partito o non è riuscito, null se tutto bene. */
  notesError: string | null = null;

  private unsubscribe = new Subject<void>();
  private cancelLoad = new Subject<void>();

  constructor(private service: LongevityRequestAssessmentsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assessment']) {
      this.detail = null;
      this.detailFailed = false;
      this.sections = [];
      this.resetNotes(null);
      this.loadDetail();
    }
  }

  areaLabel(area: LongevityAreaId): string {
    return LONGEVITY_AREA_LABELS[area];
  }

  /** Stesse icone per area della Web App (`LONGEVITY_AREA_ICONS`), classi `.i-*` definite nello scss. */
  areaIcon(area: LongevityAreaId): string {
    return LONGEVITY_AREA_ICONS[area];
  }

  gradeLabel(grade: LongevityGrade): string {
    return LONGEVITY_GRADE_LABELS[grade];
  }

  gradeClass(grade: LongevityGrade): string {
    return `grade-${grade === '-' ? 'none' : grade}`;
  }

  /** Tinta della card punteggio-area nel riepilogo della modale, separata dalla classe del badge. */
  toneClass(grade: LongevityGrade): string {
    return `tone-${grade === '-' ? 'none' : grade}`;
  }

  caseStatusLabel(status: ManagerCaseStatus | null): string {
    return status ? MANAGER_CASE_STATUS_LABELS[status] : '-';
  }

  formatScore(score: number | null): string {
    return score === null ? '-' : new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(score);
  }

  /** Le quattro aree con punteggio del riepilogo: arrivano con il dettaglio, la riga della lista ha solo i gradi. */
  get summaryAreas(): LongevityAreaScore[] {
    return this.detail ? this.detail.assessment.areas : [];
  }

  get notesDirty(): boolean {
    return this.commentControl.dirty || this.planControl.dirty;
  }

  /** Allinea gli editor al commento e al piano del server (vuoti finché il dettaglio non è arrivato). */
  private resetNotes(detail: LongevityAssessmentDetail | null): void {
    this.commentControl.setValue(markdownToEditorHtml(detail?.assessment.comment ?? []));
    this.planControl.setValue(markdownToEditorHtml(detail?.assessment.plan ?? ''));
    this.commentControl.markAsPristine();
    this.planControl.markAsPristine();
    this.notesSaved = false;
    this.notesError = null;
  }

  saveNotes(): void {
    const assessment = this.assessment;
    if (this.notesSaving) {
      return;
    }
    const comment = editorHtmlToMarkdown(this.commentControl.value);
    const plan = editorHtmlToMarkdown(this.planControl.value);
    if (comment.length > LONGEVITY_COMMENT_MAX_LENGTH) {
      this.notesError = `Il commento è troppo lungo: ${comment.length} caratteri su un massimo di ${LONGEVITY_COMMENT_MAX_LENGTH}.`;
      return;
    }
    if (plan.length > LONGEVITY_PLAN_MAX_LENGTH) {
      this.notesError = `Il piano è troppo lungo: ${plan.length} caratteri su un massimo di ${LONGEVITY_PLAN_MAX_LENGTH}.`;
      return;
    }
    this.notesSaving = true;
    this.notesSaved = false;
    this.notesError = null;
    this.service
      .updateAssessmentNotes(assessment.id, { comment, plan })
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.notesSaving = false))
      )
      .subscribe({
        next: (detail) => {
          // Se nel frattempo si è aperto un altro test, i controlli non sono più di questo.
          if (this.assessment === assessment) {
            this.detail = detail;
            this.commentControl.markAsPristine();
            this.planControl.markAsPristine();
            this.notesSaved = true;
            this.detailChanged.emit(detail);
          }
        },
        error: (err) => {
          console.error(`Longevity: salvataggio di commento e piano del test ${assessment.id} non riuscito`, err);
          if (this.assessment === assessment) {
            this.notesError = 'Salvataggio non riuscito, riprova.';
          }
        },
      });
  }

  retryDetail(): void {
    this.loadDetail();
  }

  /**
   * Risposte, risultato, commento e piano dal dettaglio del test (le righe della lista non li hanno). Il
   * questionario serve solo come dato di riferimento per i testi delle domande e, se non è accessibile
   * all'operatore, non blocca la modale.
   */
  private loadDetail(): void {
    const assessment = this.assessment;
    this.cancelLoad.next(); // un test aperto nel frattempo rende inutile la risposta in volo
    this.detailLoading = true;
    this.detailFailed = false;
    forkJoin({
      detail: this.service.getAssessmentDetail(assessment.id),
      questionnaire: this.service.getQuestionnaire().pipe(
        catchError((err) => {
          console.error('Longevity: questionario non disponibile, mostro le chiavi grezze', err);
          return of(null);
        })
      ),
    })
      .pipe(
        takeUntil(this.cancelLoad),
        takeUntil(this.unsubscribe),
        finalize(() => {
          if (this.assessment === assessment) {
            this.detailLoading = false;
          }
        })
      )
      .subscribe({
        next: ({ detail, questionnaire }) => {
          this.detail = detail;
          this.sections = groupAnswersBySection(buildAnswerViews(questionnaire, detail.answers));
          if (!this.notesDirty) {
            this.resetNotes(detail);
          }
          this.detailChanged.emit(detail);
        },
        error: (err) => {
          console.error(`Longevity: dettaglio del test ${assessment.id} non disponibile`, err);
          this.detailFailed = true;
        },
      });
  }

  /** Solo un click sullo sfondo, non sul contenuto, chiude la modale. */
  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closed.emit();
    }
  }

  ngOnDestroy(): void {
    this.cancelLoad.next();
    this.cancelLoad.complete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
